import React, { useState } from "react";
import resStyles from "../css/scanningSessionResponsive.module.scss";
import { formatTime24Hour } from "common/service/CommonService";

interface MobileScansOverlayProps {
  historyScans: any[];
  activeTab: "select" | "step1" | "step2";
  setIsShowingAllScans: (show: boolean) => void;
  totalCount?: number;
  totalrecords?: number;
  currentPage?: number;
  onPageChange?: (page: number) => void;
  searchText?: string;
  onSearchChange?: (text: string) => void;
}

const MobileScanningScansOverlay: React.FC<MobileScansOverlayProps> = ({
  historyScans = [],
  activeTab,
  setIsShowingAllScans,
  totalCount: propTotalCount,
  totalrecords,
  currentPage: propCurrentPage,
  onPageChange,
  searchText: propSearchText,
  onSearchChange,
}) => {
  const [localPage, setLocalPage] = useState(1);
  const [localSearchText, setLocalSearchText] = useState(propSearchText || "");
  const currentPage = propCurrentPage ?? localPage;
  const currentSearchText = propSearchText ?? localSearchText;
  const pageSize = 10;

  // Extract total records from prop (totalCount or totalrecords) or from the total_count property in the backend response items
  const totalCount =
    propTotalCount ??
    totalrecords ??
    (historyScans?.[0]?.total_count || historyScans?.length || 0);

  const totalPages = Math.ceil(totalCount / pageSize) || 1;

  const handlePageChange = (newPage: number) => {
    setLocalPage(newPage);
    if (onPageChange) {
      onPageChange(newPage);
    }
  };

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    setLocalSearchText(text);
    if (onSearchChange) {
      onSearchChange(text);
    }
  };

  const handleClearSearch = () => {
    setLocalSearchText("");
    if (onSearchChange) {
      onSearchChange("");
    }
  };

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Close only if clicking directly on the backdrop (not inside the modal content)
    if (e.target === e.currentTarget) {
      setIsShowingAllScans(false);
    }
  };

  return (
    <div className={resStyles.overlayBackdrop} onClick={handleBackdropClick}>
      <div className={resStyles.overlayContent}>
        {/* Header */}
        <div className={resStyles.overlayHeader}>
          <h5 className={resStyles.overlayTitle}>
            <i className="ri-history-line"></i> All Session Scans
          </h5>
          <button
            type="button"
            className={resStyles.closeOverlayBtn}
            onClick={() => setIsShowingAllScans(false)}
            aria-label="Close scans overview"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        {/* Search Box */}
        <div className={resStyles.overlayControls}>
          <div className={resStyles.searchBox}>
            <i className="ri-search-line"></i>
            <input
              type="text"
              placeholder="Search by barcode or message..."
              value={currentSearchText}
              onChange={handleSearchInputChange}
            />
            {currentSearchText && (
              <button
                type="button"
                className={resStyles.clearSearchBtn}
                onClick={handleClearSearch}
              >
                <i className="ri-close-circle-fill"></i>
              </button>
            )}
          </div>
        </div>

        {/* Scrollable Scans List */}
        <div className={resStyles.overlayScansList}>
          {historyScans.length === 0 ? (
            <div className={resStyles.emptyStateContainer}>
              <i className={`ri-search-2-line ${resStyles.emptyIcon}`}></i>
              <h6>No Scans Found</h6>
              <p>There are no session scans recorded yet.</p>
            </div>
          ) : (
            historyScans.map((row: any, idx: number) => {
              const dateStr = row.scandate
                ? new Date(row.scandate).toLocaleDateString([], {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                  })
                : "-";
              const timeStr = row.scandate
                ? new Date(row.scandate).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                    hour12: false,
                  })
                : "-";
              const resultStatus = (row.result || "valid").toLowerCase();
              const isScanValid = resultStatus === "valid";
              const isScanSkipped = resultStatus === "skipped";

              const cardClass = isScanValid
                ? resStyles.validScan
                : isScanSkipped
                ? resStyles.warningScan
                : resStyles.invalidScan;
              const badgeClass = isScanValid
                ? resStyles.validBadge
                : resStyles.invalidBadge;

              return (
                <div key={idx} className={`${resStyles.historyScanCard} ${cardClass}`}>
                  <div className={resStyles.historyScanCardHeader}>
                    <div className={resStyles.historyScanTime}>
                      <i className="ri-calendar-line"></i>
                      <span>{dateStr}</span>
                      <span className="mx-1 text-muted">•</span>
                      <i className="ri-time-line"></i>
                      <span>{timeStr}</span>
                    </div>
                    {isScanSkipped ? (
                      <span
                        className="badge bg-warning bg-opacity-10 border border-warning border-opacity-50 px-2 py-1 text-uppercase fw-bold shadow-sm"
                        style={{
                          fontSize: "11px",
                          backgroundColor: "#fff8e6",
                          color: "#b45309",
                          borderColor: "#f59e0b",
                          borderRadius: "6px",
                          padding: "3px 8px",
                        }}
                      >
                        SKIPPED
                      </span>
                    ) : (
                      <span className={`${resStyles.historyStatusBadge} ${badgeClass}`}>
                        {row.result || "valid"}
                      </span>
                    )}
                  </div>

                  <div className={resStyles.historyScanCardBody}>
                    {(activeTab === "step1" || row.subjectbarcode) && (
                      <div className={resStyles.historyScanFieldRow}>
                        <span className={resStyles.historyScanFieldLabel}>
                          <i className="ri-user-3-line"></i> Subject Barcode:
                        </span>
                        <span className={resStyles.historyScanFieldValue}>
                          {row.subjectbarcode || "-"}
                        </span>
                      </div>
                    )}

                    <div className={resStyles.historyScanFieldRow}>
                      <span className={resStyles.historyScanFieldLabel}>
                        <i className="ri-bubble-chart-line"></i> Vacutainer Barcode:
                      </span>
                      <span className={resStyles.historyScanFieldValue}>
                        {row.vaccutainerbarcode || "-"}
                      </span>
                    </div>

                    {activeTab === "step1" && (
                      <>
                        {row.dosingscheduletime && (
                          <div className={resStyles.historyScanFieldRow}>
                            <span className={resStyles.historyScanFieldLabel}>
                              <i className="ri-time-line"></i> Schedule Time:
                            </span>
                            <span className={resStyles.historyScanFieldValue}>
                              {formatTime24Hour(row.dosingscheduletime)}
                            </span>
                          </div>
                        )}
                        {row.samplecollectiontime && (
                          <div className={resStyles.historyScanFieldRow}>
                            <span className={resStyles.historyScanFieldLabel}>
                              <i className="ri-drop-line"></i> Collected Time:
                            </span>
                            <span className={resStyles.historyScanFieldValue}>
                              {formatTime24Hour(row.samplecollectiontime)}
                            </span>
                          </div>
                        )}
                      </>
                    )}

                    {(activeTab === "step2" || row.aliquotbarcode) && (
                      <div className={resStyles.historyScanFieldRow}>
                        <span className={resStyles.historyScanFieldLabel}>
                          <i className="ri-test-tube-line"></i> Aliquot Barcode:
                        </span>
                        <span className={resStyles.historyScanFieldValue}>
                          {row.aliquotbarcode || "-"}
                        </span>
                      </div>
                    )}

                    {row.resultmessage && (
                      <div
                        className={`${resStyles.historyResultMsgBanner} ${
                          isScanValid ? resStyles.validMsg : resStyles.invalidMsg
                        }`}
                      >
                        <i
                          className={
                            isScanValid ? "ri-checkbox-circle-fill" : "ri-error-warning-fill"
                          }
                        ></i>
                        <span>{row.resultmessage}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Pagination Footer */}
        {totalCount > pageSize && (
          <div className={resStyles.overlayPagination}>
            <button
              type="button"
              className={resStyles.paginationBtn}
              disabled={currentPage === 1}
              onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
            >
              <i className="ri-arrow-left-s-line"></i> Prev
            </button>
            <span className={resStyles.paginationInfo}>
              Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong> ({totalCount} total)
            </span>
            <button
              type="button"
              className={resStyles.paginationBtn}
              disabled={currentPage >= totalPages}
              onClick={() => handlePageChange(Math.min(currentPage + 1, totalPages))}
            >
              Next <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MobileScanningScansOverlay;
